import React, { useCallback, useMemo, useRef } from 'react';
import { DropzoneRef } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { validateFamilyBooking } from '../../../util/validateFamilyBooking';
import {
  getCurrentlySelectedLocationFromState,
  isPhotoIdBackRequired,
  isPhotoIdFrontRequired,
} from '~/core/util/location';
import {
  setMetaPaperworkPhotoIdFrontId,
  setMetaPaperworkPhotoIdBackId,
  setMetaPaperworkPhotoIdFrontImgUrl,
  setMetaPaperworkPhotoIdBackImgUrl,
} from '~/ducks/paperworkMeta';
import {
  receiveBackPhotoId,
  receiveFrontPhotoId,
  setPhotoIdBackImageData,
  setPhotoIdFrontImageData,
  uploadingPhotoId,
} from '~/ducks/photoId';
import { CopyImageSagaAction, ImagesSaga, ImageType, UploadImageSagaAction } from '~/sagas/images';
import { useSolvSelector } from '~/reducers';
import DropzoneUploader from '../components/DropzoneUploader';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import { useEvent } from '~/components/Paperwork/hooks';
import { getProfileFromBooking } from '~/core/util/account';

export type Side = 'front' | 'back';
type CardStatus = 'blank' | 'loading' | 'success';

export type CopyPhotoIdParams = {
  imageType: ImageType;
  clear: boolean;
  cardId: string;
  imageUrl: string;
};

/**
 * Hook providing components, state, and handlers necessary to build various types of
 * photo id upload flows.
 */
export function usePhotoIdUpload({
  hideInputs,
  onDropSideEffect,
}: { hideInputs?: boolean; onDropSideEffect?: (val: boolean) => void } = {}) {
  const { location, photoId } = useSolvSelector((state) => ({
    location: getCurrentlySelectedLocationFromState(state),
    photoId: state.photoId,
  }));

  const bookingId = usePaperworkContextBookingId();
  const booking = useSolvSelector((state) => state.pastBookings[bookingId]);
  const { isPrimary } = validateFamilyBooking(booking);
  const accountSummary = useSolvSelector((state) => state.account.summary);
  const userProfile = getProfileFromBooking(accountSummary, booking);

  const getStatus = useCallback(
    (side: Side): CardStatus => {
      if (photoId.loading) return 'loading';

      const data = photoId[side];
      if (data?.id && data?.imageData) return 'success';
      return 'blank';
    },
    [photoId]
  );

  const status = useMemo<Record<Side, CardStatus>>(
    () => ({
      front: getStatus('front'),
      back: getStatus('back'),
    }),
    [getStatus]
  );

  const isRequired = isPhotoIdFrontRequired(location) || isPhotoIdBackRequired(location);

  const dispatch = useDispatch();

  const copyPhotoIdToState = useEvent(
    ({ imageType, clear, cardId = '', imageUrl = '' }: CopyPhotoIdParams) => {
      const params =
        imageType === ImageType.PhotoIdFront
          ? { setImageData: [setPhotoIdFrontImageData], setImageId: [receiveFrontPhotoId] }
          : { setImageData: [setPhotoIdBackImageData], setImageId: [receiveBackPhotoId] };
      dispatch<CopyImageSagaAction>({
        imageUrl: clear ? '' : imageUrl,
        cardId: clear ? '' : cardId,
        ...params,
        onStart: () => uploadingPhotoId(true),
        onStop: () => uploadingPhotoId(false),
        type: ImagesSaga.Copy,
      });
    }
  );

  const handleUpload = (type: Side) => (image: File) => {
    if (!image) return;

    const imageType = type === 'front' ? ImageType.PhotoIdFront : ImageType.PhotoIdBack;

    const params =
      imageType === ImageType.PhotoIdFront
        ? {
            setImageData: [
              setPhotoIdFrontImageData,
              ...(isPrimary ? [setMetaPaperworkPhotoIdFrontImgUrl] : []),
            ],
            setImageId: [
              receiveFrontPhotoId,
              ...(isPrimary ? [setMetaPaperworkPhotoIdFrontId] : []),
            ],
          }
        : {
            setImageData: [
              setPhotoIdBackImageData,
              ...(isPrimary ? [setMetaPaperworkPhotoIdBackImgUrl] : []),
            ],
            setImageId: [receiveBackPhotoId, ...(isPrimary ? [setMetaPaperworkPhotoIdBackId] : [])],
          };
    dispatch<UploadImageSagaAction>({
      imageFile: image,
      imageType,
      onStart: () => uploadingPhotoId(true),
      onStop: () => uploadingPhotoId(false),
      ...params,
      shouldReorient: true,
      type: ImagesSaga.Upload,
      userProfile,
    });
  };

  const frontRef = useRef<DropzoneRef>(null);
  const backRef = useRef<DropzoneRef>(null);

  const Inputs = (
    <>
      <DropzoneUploader
        handleUpload={handleUpload('front')}
        hidden={hideInputs}
        imageData={photoId.front?.imageData}
        onDropSideEffect={onDropSideEffect}
        ref={frontRef}
        side="front"
      />
      <DropzoneUploader
        handleUpload={handleUpload('back')}
        hidden={hideInputs}
        imageData={photoId.back?.imageData}
        onDropSideEffect={onDropSideEffect}
        ref={backRef}
        side="back"
      />
    </>
  );

  const done = useMemo(() => {
    if (isPhotoIdFrontRequired(location) && status.front !== 'success') {
      return false;
    }

    if (isPhotoIdBackRequired(location) && status.back !== 'success') {
      return false;
    }

    return true;
  }, [location, status.back, status.front]);

  return {
    Inputs,
    isRequired,
    handleUpload,
    front: {
      status: status.front,
      image: photoId.front?.imageData,
      uploaded: status.front === 'success',
      triggerUpload: () => frontRef.current?.open(),
    },
    back: {
      status: status.back,
      image: photoId.back?.imageData,
      uploaded: status.back === 'success',
      triggerUpload: () => backRef.current?.open(),
    },
    loading: photoId.loading,
    done,
    copyPhotoIdToState,
  };
}
